/**
 * The table layout drafted on the quick setup page (FR-TBL-01): areas with their tables, all editable before anything
 * is saved. Codes skip what the store already uses and what the draft already has.
 */
export type Shape = 'rect' | 'square' | 'round';

export interface DraftTable {
  key: string;
  code: string;
  capacity: number;
  shape: Shape;
}

export interface DraftArea {
  key: string;
  code: string;
  name: string;
  tables: DraftTable[];
}

export interface Taken {
  areaCodes: ReadonlySet<string>;
  tableCodes: ReadonlySet<string>;
}

export interface GenerateOptions {
  areas: number;
  tablesPerArea: number;
  capacity: number;
  shape: Shape;
  /** The dummy name of the n-th area of the store, e.g. "Area 3". */
  areaName(n: number): string;
  taken: Taken;
  /** Areas the store already has, so new names continue from there. */
  existingAreas?: number;
}

let counter = 0;
const nextKey = () => `k${++counter}`;

export const normalize = (code: string) => code.trim().toUpperCase();

/** A, B, ... Z, AA, AB ... */
export function letters(index: number): string {
  let n = index + 1;
  let text = '';
  while (n > 0) {
    const r = (n - 1) % 26;
    text = String.fromCharCode(65 + r) + text;
    n = Math.floor((n - 1) / 26);
  }
  return text;
}

function usedCodes(draft: readonly DraftArea[], taken: Taken, except?: DraftTable[]) {
  const used = new Set(taken.tableCodes);
  for (const area of draft) for (const table of area.tables) if (!except?.includes(table)) used.add(normalize(table.code));
  return used;
}

function nextAreaCode(draft: readonly DraftArea[], taken: Taken): string {
  const used = new Set([...taken.areaCodes, ...draft.map((a) => normalize(a.code))]);
  for (let i = 0; ; i++) if (!used.has(letters(i))) return letters(i);
}

/** Table codes for an area: its code and a two-digit number (A01, A02...), skipping codes in use. */
function tableCodes(areaCode: string, count: number, used: Set<string>, start = 1): string[] {
  const codes: string[] = [];
  for (let n = start; codes.length < count; n++) {
    const code = `${normalize(areaCode)}${String(n).padStart(2, '0')}`;
    if (!used.has(code)) {
      used.add(code);
      codes.push(code);
    }
  }
  return codes;
}

export function generateDraft(options: GenerateOptions): DraftArea[] {
  const draft: DraftArea[] = [];
  for (let i = 0; i < options.areas; i++) {
    draft.push(
      newArea(draft, options.taken, {
        name: options.areaName((options.existingAreas ?? 0) + i + 1),
        tables: options.tablesPerArea,
        capacity: options.capacity,
        shape: options.shape,
      }),
    );
  }
  return draft;
}

export function newArea(
  draft: readonly DraftArea[],
  taken: Taken,
  { name, tables, capacity, shape }: { name: string; tables: number; capacity: number; shape: Shape },
): DraftArea {
  const code = nextAreaCode(draft, taken);
  const used = usedCodes(draft, taken);
  return {
    key: nextKey(),
    code,
    name,
    tables: tableCodes(code, tables, used).map((c) => ({ key: nextKey(), code: c, capacity, shape })),
  };
}

/** Adds tables at the end (numbered after the last) or removes them from the end. */
export function setTableCount(draft: readonly DraftArea[], areaKey: string, count: number, taken: Taken): DraftArea[] {
  return draft.map((area) => {
    if (area.key !== areaKey) return area;
    if (count <= area.tables.length) return { ...area, tables: area.tables.slice(0, Math.max(0, count)) };
    const last = area.tables.at(-1);
    const template = { capacity: last?.capacity ?? 4, shape: last?.shape ?? ('square' as Shape) };
    const added = tableCodes(area.code, count - area.tables.length, usedCodes(draft, taken), area.tables.length + 1);
    return { ...area, tables: [...area.tables, ...added.map((code) => ({ key: nextKey(), code, ...template }))] };
  });
}

/** Numbers the area's tables again from its code: after the area's code changes, or tables were removed. */
export function renumber(draft: readonly DraftArea[], areaKey: string, taken: Taken): DraftArea[] {
  return draft.map((area) => {
    if (area.key !== areaKey) return area;
    const codes = tableCodes(area.code, area.tables.length, usedCodes(draft, taken, area.tables));
    return { ...area, tables: area.tables.map((t, i) => ({ ...t, code: codes[i] ?? t.code })) };
  });
}

export type Problem = 'required' | 'format' | 'duplicate' | 'taken' | 'capacity';

const CODE_FORMAT = /^[A-Za-z0-9_.-]+$/;

/** What stops the draft from being saved, by the key of the area or table. */
export function findProblems(draft: readonly DraftArea[], taken: Taken): Map<string, Problem> {
  const problems = new Map<string, Problem>();
  const count = (codes: string[]) => codes.reduce((m, c) => m.set(c, (m.get(c) ?? 0) + 1), new Map<string, number>());
  const areaCounts = count(draft.map((a) => normalize(a.code)));
  const tableCounts = count(draft.flatMap((a) => a.tables.map((t) => normalize(t.code))));
  const check = (key: string, code: string, counts: Map<string, number>, takenCodes: ReadonlySet<string>) => {
    const c = normalize(code);
    if (!c) problems.set(key, 'required');
    else if (!CODE_FORMAT.test(c)) problems.set(key, 'format');
    else if ((counts.get(c) ?? 0) > 1) problems.set(key, 'duplicate');
    else if (takenCodes.has(c)) problems.set(key, 'taken');
  };
  for (const area of draft) {
    check(area.key, area.code, areaCounts, taken.areaCodes);
    if (!area.name.trim() && !problems.has(area.key)) problems.set(`${area.key}:name`, 'required');
    for (const table of area.tables) {
      check(table.key, table.code, tableCounts, taken.tableCodes);
      if (!problems.has(table.key) && !(table.capacity >= 1 && table.capacity <= 100)) problems.set(table.key, 'capacity');
    }
  }
  return problems;
}

/** The request body for saving. */
export function toRequest(draft: readonly DraftArea[]) {
  return {
    areas: draft.map((a) => ({
      code: normalize(a.code),
      name: a.name.trim(),
      tables: a.tables.map((t) => ({ code: normalize(t.code), capacity: t.capacity, shape: t.shape })),
    })),
  };
}
